/**
 * Exploit Direction — "vs <archetype>, should you <dimension> more,
 * less, or same as baseline?"
 *
 * Pulls directly from the per-archetype "Two-Rule Adjustment" sections
 * in sheets 9–14 and the exploit framework in sheet 8. Each question
 * tests one (archetype, dimension) → direction pair.
 *
 * 3-option MC ("more" / "less" / "same") — the smallest possible
 * answer space, which is the point: you don't get fancy when applying
 * an exploit, you pick a direction.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type Direction = "more" | "less" | "same";

export interface ExploitDirectionQuestion {
  prompt: string;
  /** The direction the sheet endorses. */
  answer: Direction;
  rationale: string;
}

export const EXPLOIT_DIRECTION_BANK: ExploitDirectionQuestion[] = [
  // STATION
  {
    prompt: "vs Station: should you bluff MORE, LESS, or the SAME as baseline?",
    answer: "less",
    rationale: "Sheet 9: stations don't fold, so your bluffs don't fold them out. Drop bluff frequency toward zero against confirmed stations.",
  },
  {
    prompt: "vs Station: should you value bet THINNER, the same, or TIGHTER (only nuts)?",
    answer: "more",
    rationale: "Sheet 9 Rule 1: value thinner AND bigger. Stations will pay off worse hands you wouldn't normally value bet: second pair, weak top pairs, etc.",
  },
  {
    prompt: "vs Station: should value-bet sizing be BIGGER, the same, or SMALLER than baseline?",
    answer: "more",
    rationale: "Stations are price-insensitive: they call regardless of size. Bigger value bets extract more without losing the call.",
  },
  {
    prompt: "vs Station: should you defend light (bluff-catch wider) MORE, LESS, or SAME?",
    answer: "less",
    rationale: "Stations rarely bluff, so your bluff-catching threshold should tighten: fold marginal made hands against their river bets.",
  },

  // NIT
  {
    prompt: "vs Nit: should you bluff MORE, LESS, or SAME as baseline?",
    answer: "more",
    rationale: "Sheet 11 Rule 1: nits fold above MDF, so bluffs print. Up your bluff frequency, especially on scary cards and in spots where their range is weak.",
  },
  {
    prompt: "vs Nit: should you fold strong hands to their sustained aggression MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Sheet 11 Rule 2: when a nit fires multiple streets, they have it. Overpairs and TPTK are bluff catchers vs balanced ranges but folds vs a nit's value-only barrel.",
  },
  {
    prompt: "vs Nit: should you 3-bet light (with bluffs) MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Nits open tight and fold to 3-bets at a high frequency. Increase your 3-bet bluff frequency to attack their fold-too-much tendency.",
  },
  {
    prompt: "vs Nit: should you value bet thin/marginal hands MORE, LESS, or SAME?",
    answer: "less",
    rationale: "Nits only call with hands that beat marginal value. Tighten your thin value range against them: they'll often have you outkicked or beat.",
  },

  // MANIAC
  {
    prompt: "vs Maniac: should you call wider (looser bluff-catch) MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Sheet 12 Rule 2: maniacs over-bluff, so their bet ranges contain more air. Call wider, especially on rivers and against polarized sizings.",
  },
  {
    prompt: "vs Maniac: should you bluff MORE, LESS, or SAME?",
    answer: "less",
    rationale: "Maniacs are stations against your bluffs because they don't fold. Their VPIP-tight tendency only applies to opening; postflop they hero-call light. Don't bluff into them.",
  },
  {
    prompt: "vs Maniac: should you trap with strong hands (slowplay) MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Sheet 12 Rule 1: maniacs bet their own air into your traps. Slow-play more, letting them barrel into your nuts on multiple streets.",
  },
  {
    prompt: "vs Maniac: should you 4-bet light preflop MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Their 3-bet range is wide and weak. 4-betting light printsfold equity + lets you stack their bluffs when they continue light.",
  },

  // LIMPER / WHALE
  {
    prompt: "vs Limper/Whale: should you iso-raise wider MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Sheet 10: limpers cap their preflop range below pairs/strong broadways. Iso wider, since you're heads-up with position against a weak range.",
  },
  {
    prompt: "vs Limper/Whale: should you value bet thinner MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Whales call with hands they shouldn't (pair-no-kicker, gutshots, ace-high), so thinner value extracts from a wider calling range.",
  },
  {
    prompt: "vs Limper/Whale: should you bluff MORE, LESS, or SAME?",
    answer: "less",
    rationale: "Limpers/whales are sticky postflop. They call flop and turn bets light. Bluffs don't fold them out, so stick to value extraction.",
  },

  // TILTED (steaming-aggressive variant)
  {
    prompt: "vs Tilted opponent (steaming, betting wildly): should you hero-call MORE, LESS, or SAME?",
    answer: "more",
    rationale: "Sheet 13: tilt-aggressive players bluff far more than their baseline. Call them lighter, especially on rivers, because their bet ranges are air-heavy.",
  },
  {
    prompt: "vs Tilted opponent (steaming): should value-bet sizing be BIGGER, SMALLER, or SAME?",
    answer: "more",
    rationale: "Steaming opponents are price-insensitive. Bigger value bets get called by hands that would fold to standard sizing against a sober player.",
  },
  {
    prompt: "vs Tilted opponent (steaming): should you bluff MORE, LESS, or SAME?",
    answer: "less",
    rationale: "Steaming tilt makes opponents call-stationy too, so they won't fold. Don't try to push them off; let them spew into your value range.",
  },

  // SOLID REG
  {
    prompt: "vs Solid Reg: should you deviate from GTO baseline MORE, LESS, or NOT AT ALL?",
    answer: "same",
    rationale: "Sheet 14: regs play balanced ranges, so the baseline (GTO) is the right answer. Exploits demand evidence of imbalance, and regs don't give you that.",
  },
  {
    prompt: "vs Solid Reg: should you bluff MORE, LESS, or SAME as baseline?",
    answer: "same",
    rationale: "Bluffing more requires evidence the reg over-folds, and bluffing less requires they over-call. Without that evidence: stay at the baseline frequency.",
  },
  {
    prompt: "vs Solid Reg: should you value bet thinner MORE, LESS, or SAME?",
    answer: "same",
    rationale: "Thin value requires evidence the opponent calls too wide. A balanced reg doesn't: they'll fold the bottom of their range. Value bet at baseline thickness.",
  },
];

export function generateQuestion(
  rand: () => number = Math.random,
): ExploitDirectionQuestion {
  return pickFrom(EXPLOIT_DIRECTION_BANK, rand);
}

export const _internals = { EXPLOIT_DIRECTION_BANK };
